import { useCallback, useEffect, useState } from 'react';
import { loadAccessToken, loadLocalOrder, saveLocalOrder } from './storage';
import type { CreateMusicOrder, Order } from './types';

export function useCreateMusicOrder(slug: string, pollMs = 0) {
  const [order, setOrder] = useState<CreateMusicOrder | null>(() => loadLocalOrder(slug));
  const [loading, setLoading] = useState(!order);
  const [error, setError] = useState('');

  const refresh = useCallback(async () => {
    const token = loadAccessToken(slug);
    if (!slug || !token) { setLoading(false); setError('Este pedido precisa ser reaberto no mesmo navegador.'); return null; }
    try {
      const response = await fetch(`/api/criar-musica?action=info&slug=${encodeURIComponent(slug)}`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'Pedido não encontrado.');
      setOrder(data.order); saveLocalOrder(data.order); setError(''); return data.order as CreateMusicOrder;
    } catch (reason) { setError(reason instanceof Error ? reason.message : 'Não foi possível abrir o pedido.'); return null; }
    finally { setLoading(false); }
  }, [slug]);

  useEffect(() => { void refresh(); }, [refresh]);
  useEffect(() => {
    if (!pollMs) return undefined;
    const timer = window.setInterval(() => void refresh(), pollMs);
    return () => window.clearInterval(timer);
  }, [pollMs, refresh]);

  return { order, setOrder, loading, error, refresh };
}

export function useOrder(slug: string, options: { pollMs?: number } = {}) {
  const state = useCreateMusicOrder(slug, options.pollMs || 0);
  const order: Order | null = state.order ? {
    ...state.order,
    recipientName: state.order.form.recipientName,
    previewUrl: state.order.previewAudioUrl,
  } : null;
  return { ...state, order };
}
